import { useState } from 'react';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useUpdateNumberMutation } from '../api/numbersApi';
import { numbersErrorMessage } from '../utils';
import type { PhoneNumberView } from '../types';

export interface StatusToggleProps {
  number: PhoneNumberView;
  /** Resolved bound-agent name for the deactivate-confirm copy (§10). */
  agentName?: string;
  /** `voice:numbers:update` verdict for `org:${orgId}` (undefined = loading). */
  allowed: boolean | undefined;
  /** Tooltip text when denied (design §6.3 deny rule applied to the switch). */
  deniedHint: string;
}

/**
 * Inline active/inactive switch — the ONE activation control (design §10, H3).
 * Mirrors the P2 `role="switch"` button idiom verbatim (`attach-storage.step
 * .tsx`); the 20×20 visual sits in a ≥40×40 hit zone via the `after` pseudo
 * (design §6.2/C1, §17). Toggle-off on a BOUND number confirms first (audit #6
 * — the forwarding consequence); unbound flips immediately; toggling on an
 * optimistic `pending` state with `aria-busy` while the PATCH is in flight.
 */
export function StatusToggle({ number, agentName, allowed, deniedHint }: StatusToggleProps) {
  const toast = useToast();
  const [updateNumber, { isLoading }] = useUpdateNumberMutation();
  const [pending, setPending] = useState<boolean | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const shown = pending ?? number.isActive;
  const denied = allowed !== true;

  async function commit(next: boolean) {
    setPending(next);
    try {
      await updateNumber({ id: number.id, changes: { isActive: next } }).unwrap();
      toast.success(next ? 'Number activated.' : 'Number deactivated.');
    } catch (error) {
      toast.error(
        numbersErrorMessage(
          error,
          next ? "Couldn't activate number." : "Couldn't deactivate number.",
        ),
      );
    } finally {
      setPending(null);
    }
  }

  function flip(next: boolean) {
    if (denied || isLoading || pending !== null) {
      return;
    }
    if (!next && number.factoryId) {
      // Reversible state change — default variant, not destructive (§10/B1).
      setConfirmOpen(true);
      return;
    }
    void commit(next);
  }

  const switchButton = (
    <button
      type="button"
      role="switch"
      aria-checked={shown}
      aria-label={`Active status for ${number.number}`}
      aria-busy={pending !== null || isLoading || undefined}
      disabled={denied}
      onClick={() => flip(!shown)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors motion-reduce:transition-none after:absolute after:-inset-2.5 after:content-[''] disabled:cursor-not-allowed disabled:opacity-50 ${
        shown ? 'border-primary bg-primary' : 'border-input bg-input'
      }`}
    >
      <span
        aria-hidden="true"
        className={`block h-4 w-4 rounded-full bg-background transition-transform motion-reduce:transition-none ${
          shown ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );

  return (
    <>
      {denied ? (
        // P2 deny rule: disabled + Tooltip. The span trigger receives hover
        // events the disabled button swallows.
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">{switchButton}</span>
          </TooltipTrigger>
          <TooltipContent>{deniedHint}</TooltipContent>
        </Tooltip>
      ) : (
        switchButton
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Deactivate number?"
        description={`Calls to ${number.number} will stop forwarding to ${
          agentName ?? 'its bound agent'
        } until you reactivate it.`}
        confirmLabel="Deactivate"
        isLoading={isLoading}
        onConfirm={() => {
          setConfirmOpen(false);
          void commit(false);
        }}
      />
    </>
  );
}
